import { zodResolver } from "@hookform/resolvers/zod";
import { FieldPath, useForm } from "react-hook-form";
import FormFieldInput from "@/src/components/FormField/FormFieldInput";
import FormFieldSelect from "@/src/components/FormField/FormFieldSelect";
import FormFieldColor from "@/src/components/FormField/FormFieldColor";
import FormFieldImage from "@/src/components/FormField/FormFieldImage";
import { Form } from "@/components/ui/form";
import Button from "@/src/components/Button/Button";
import FormSection from "@/src/components/FormSection/FormSection";
import { EditTenantFormValuesType, editTenantFormSchema } from "../schemas";
import { DEFAULT_PRIMARY_COLOR } from "@/src/lib/theme";
import { tenantSlug } from "@/src/lib/tenantSlug";
import { formatPostalCode } from "@/src/lib/postalCode";

const MAP_CRS_OPTIONS = [
  { value: "PL2000", label: "Układy 2000 (EPSG:2176–2179, auto-wybór strefy)" },
  { value: "PL1992", label: "Układ 1992 / CS92 (EPSG:2180)" },
] as const;

type EditTenantFormPropsType = {
  onSubmit: (values: EditTenantFormValuesType) => void;
  defaultValues?: EditTenantFormValuesType;
};

function EditTenantForm({ onSubmit, defaultValues }: EditTenantFormPropsType) {
  const form = useForm<EditTenantFormValuesType>({
    resolver: zodResolver(editTenantFormSchema),
    defaultValues: defaultValues || {
      id: "",
      displayName: "",
      country: "Polska",
      voivodeship: "",
      district: "",
      tenant: "",
      street: "",
      houseNumber: "",
      town: "",
      postalCode: "",
      mapCrs: "PL2000",
      primaryColor: DEFAULT_PRIMARY_COLOR,
    },
  });

  const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const displayName = e.target.value;
    form.setValue("displayName", displayName, { shouldValidate: true, shouldDirty: true });
    form.setValue("id", tenantSlug(displayName), { shouldValidate: true, shouldDirty: true });
  };

  const handlePostalCodeChange =
    (fieldName: FieldPath<EditTenantFormValuesType>) => (e: React.ChangeEvent<HTMLInputElement>) => {
      form.setValue(fieldName, formatPostalCode(e.target.value), {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormSection title="Edytuj gminę">
          <FormFieldInput
            form={form}
            description="Wyświetlana nazwa gminy"
            placeholder="Nazwa gminy"
            label="Nazwa gminy"
            name="displayName"
            onChange={handleDisplayNameChange}
          />
          <FormFieldInput
            form={form}
            description="Część publicznego adresu strony, np. wniosekdoplanu.pl/nowebrzesko"
            placeholder="np. nowebrzesko"
            label="Adres URL gminy"
            name="id"
          />
          <FormSection title="Dane kontaktowe" isCollapsible defaultOpen>
            <FormFieldInput form={form} description="" placeholder="Polska" label="Kraj" name="country" />
            <FormFieldInput form={form} description="" placeholder="Województwo" label="Województwo" name="voivodeship" />
            <FormFieldInput form={form} description="" placeholder="Powiat" label="Powiat" name="district" />
            <FormFieldInput form={form} description="" placeholder="Gmina" label="Gmina" name="tenant" />
            <div className="flex gap-2">
              <FormFieldInput
                form={form}
                description=""
                placeholder="Ulica"
                label="Ulica"
                name="street"
                className="w-1/2"
              />
              <FormFieldInput
                form={form}
                description=""
                placeholder="Numer domu"
                label="Numer domu"
                name="houseNumber"
                className="w-1/4"
              />
              <FormFieldInput
                form={form}
                description=""
                placeholder="Numer mieszkania"
                label="Numer mieszkania"
                name="apartmentNumber"
                className="w-1/4"
              />
            </div>
            <FormFieldInput form={form} description="" placeholder="Miasto" label="Miasto" name="town" />
            <FormFieldInput
              form={form}
              description=""
              placeholder="Kod pocztowy"
              label="Kod pocztowy"
              name="postalCode"
              onChange={handlePostalCodeChange("postalCode")}
            />
          </FormSection>
          <FormSection title="Ustawienia mapy" isCollapsible defaultOpen>
            <FormFieldSelect
              form={form}
              name="mapCrs"
              label="Układ współrzędnych mapy"
              placeholder="Wybierz układ"
              options={MAP_CRS_OPTIONS}
              description="PL-2000 daje większą dokładność w obrębie strefy (centralna i wschodnia Polska). PL-1992 pokrywa cały kraj jedną projekcją."
            />
          </FormSection>
          <FormSection title="Wygląd strony publicznej" isCollapsible defaultOpen>
            <FormFieldColor
              form={form}
              name="primaryColor"
              label="Kolor motywu"
              description="Kolor nagłówka i akcentów tekstu na publicznej stronie gminy. Kliknij kwadrat obok aby otworzyć koło kolorów, wpisz kod HEX (#rrggbb) lub wybierz z palety. Przyciski pozostają w neutralnej szarej palecie."
            />
            <FormFieldImage
              form={form}
              name="logo"
              label="Herb / logo gminy"
              description="Wyświetlane po lewej stronie nagłówka na publicznej stronie gminy. Najlepiej PNG/SVG z przezroczystym tłem. Maks. 5 MB."
              maxSizeKb={5120}
            />
          </FormSection>
        </FormSection>
        <Button type="submit">Zapisz</Button>
      </form>
    </Form>
  );
}

export default EditTenantForm;
